import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DataTablesResponse } from '../models/datatables';
import { Select2Data } from 'ng-select2-component';
import { environment } from '../../environments/environment';
import { PuntoapuntoPollDTO, PuntoapuntoPollUserDTO } from '../models/puntoapunto-poll-dto';
import { FormDataDTO } from '../models/form-data-dto';

@Injectable({
  providedIn: 'root'
})

export class PollService {

  public static readonly PUMPKIN_FOUND_LABEL = 'P7_¿Encontró la calabaza?';
  public static readonly PUMPKIN_FOUND_VALUE = 'Sí';
  public static readonly PUMPKIN_DEFAULT_VALUE = 'No';
  public static readonly PUMPKIN_FIELD_ID = 'pregunta7';

  private pathAPI = environment.urlAPI;
  private controller = "PuntoapuntoPoll/";

  constructor(private http: HttpClient) { }

  public get(id: string): Observable<PuntoapuntoPollDTO> {
    return this.http.get<PuntoapuntoPollDTO>(this.pathAPI + this.controller + id);
  }

  public post(data: FormData): Observable<any> {
    return this.http.post<any>(this.pathAPI + this.controller, data);
  }

  public put(id: string, data: FormData): Observable<any> {
    return this.http.put<any>(this.pathAPI + this.controller + id, data);
  }

  public changeStatus(id: string, idStatus: number): Observable<any> {
    return this.http.put<any>(this.pathAPI + this.controller + 'ChangeStatus/' + id + '/' + idStatus, null);
  }

  public getDataTable(data: any): Observable<DataTablesResponse> {
    return this.http.post<DataTablesResponse>(this.pathAPI + this.controller + 'GetDataTable/', data);
  }

  public getPollUserFilled(idPoll: string): Observable<boolean> {
    return this.http.get<boolean>(this.pathAPI + this.controller + 'GetPollUserFilled/' + idPoll);
  }

  public getPollUser(idPoll: string): Observable<PuntoapuntoPollUserDTO> {
    return this.http.get<PuntoapuntoPollUserDTO>(this.pathAPI + this.controller + 'GetPollUser/' + idPoll);
  }

  public GetResultCount(id: string): Observable<number> {
    return this.http.get<number>(this.pathAPI + this.controller + 'GetResultCount/' + id);
  }

  public postPollUser(data: PuntoapuntoPollUserDTO): Observable<any> {
    return this.http.post<any>(this.pathAPI + this.controller + "PostUserPoll", data);
  }

  public putPollUser(data: PuntoapuntoPollUserDTO): Observable<PuntoapuntoPollUserDTO> {
    return this.http.put<PuntoapuntoPollUserDTO>(this.pathAPI + this.controller + "PutUserPoll", data);
  }

  downloadPollResult(idPoll): Observable<Blob> {
    let requestOptions: Object = {
      responseType: 'blob'
    }
    let endpoint = this.pathAPI + this.controller + 'GetPollResult/' + idPoll;
    return this.http.get<Blob>(endpoint, requestOptions);
  }

  public GetSelectList(): Observable<Select2Data> {
    return this.http.get<Select2Data>(this.pathAPI + this.controller + 'GetSelectList');
  }

  public parsePollUserData(data: string): FormDataDTO[] {
    if (!data) {
      return [];
    }
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  public isPumpkinFoundInData(data: string): boolean {
    const entry = this.findPumpkinEntry(this.parsePollUserData(data));
    if (!entry?.VALUE) {
      return false;
    }
    return entry.VALUE.trim() === PollService.PUMPKIN_FOUND_VALUE;
  }

  public updatePumpkinToSi(pollUser: PuntoapuntoPollUserDTO): PuntoapuntoPollUserDTO {
    const list = this.parsePollUserData(pollUser.Data);
    const entry = this.findPumpkinEntry(list);
    if (entry) {
      entry.VALUE = PollService.PUMPKIN_FOUND_VALUE;
    } else {
      const maxOrder = list.reduce((max, item) => Math.max(max, item.ORDER ?? 0), 0);
      list.push(new FormDataDTO(
        PollService.PUMPKIN_FOUND_LABEL,
        'hidden',
        PollService.PUMPKIN_FOUND_VALUE,
        PollService.PUMPKIN_FIELD_ID,
        '',
        maxOrder + 1,
        null,
        null
      ));
    }
    pollUser.Data = JSON.stringify(list);
    return pollUser;
  }

  public markPumpkinFound(idPoll: string): Observable<void> {
    return this.getPollUser(idPoll).pipe(
      map((pollUser) => this.updatePumpkinToSi(pollUser)),
      switchMap((pollUser) => this.putPollUser(pollUser)),
      map(() => undefined)
    );
  }

  private findPumpkinEntry(list: FormDataDTO[]): FormDataDTO | undefined {
    return list.find(
      (item) => item.LABEL === PollService.PUMPKIN_FOUND_LABEL || item.ID === PollService.PUMPKIN_FIELD_ID
    );
  }

}
